import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { fetchMyTasks } from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { formatDateInZone, formatTimeInZone } from '../lib/format';
import { formatPriority, sortTasks } from '../lib/task-events';
import { ASAL_TUGAS_SAYA } from '../lib/task-back-link';
import { formatTaskStatus, isTerminal, type FieldTaskStatus } from '../lib/task-actions';
import { taskMark, taskStruck } from '../lib/task-row';
import { EmptyState, ErrorCard, LoadingState } from '../states';

/** Saringan yang benar-benar dipakai di lapangan — bukan seluruh delapan status. */
const FILTERS = [
  { value: '', label: 'Aktif' },
  { value: 'assigned', label: 'Ditugaskan' },
  { value: 'accepted', label: 'Diterima' },
  { value: 'started', label: 'Berjalan' },
  { value: 'arrived', label: 'Sampai lokasi' },
] as const;

/**
 * Tugas Saya — daftar milik pelaksana.
 *
 * Diurutkan prioritas lalu jadwal, bukan menurut waktu dibuat: yang menentukan apa yang
 * dikerjakan lebih dulu adalah kepentingannya, bukan kapan dispatcher mengetiknya.
 */
export function MyTasksPage(): ReactNode {
  const session = useSession();
  const timezone = session.policy?.timezone ?? 'Asia/Jakarta';
  const [status, setStatus] = useState('');

  const { state, reload } = useAsync(
    () => fetchMyTasks(session.auth, status || undefined),
    [session.auth, status],
  );

  return (
    <>
      <section className="card">
        <h2>Tugas saya</h2>
        <div className="row" role="group" aria-label="Saring status">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              className={status === f.value ? 'chip active' : 'chip'}
              onClick={() => setStatus(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </section>

      {state.status === 'loading' ? <LoadingState label="Memuat tugas…" /> : null}
      {state.status === 'error' ? <ErrorCard error={state.error} onRetry={reload} /> : null}

      {state.status === 'ready'
        ? (() => {
            // Saringan "Aktif" menyembunyikan yang sudah selesai — daftar lapangan harus berisi
            // apa yang masih harus dikerjakan, bukan arsip.
            const visible =
              status === ''
                ? state.data.filter((t) => !isTerminal(t.status as FieldTaskStatus))
                : state.data;

            if (visible.length === 0) {
              return (
                <EmptyState
                  title="Tidak ada tugas"
                  hint={
                    status === ''
                      ? 'Belum ada tugas aktif untuk Anda. Tugas baru akan muncul di sini setelah dispatcher menugaskannya.'
                      : 'Tidak ada tugas pada status ini. Ubah saringan untuk melihat yang lain.'
                  }
                />
              );
            }

            return (
              <ul className="list">
                {sortTasks(visible).map((task) => (
                  <li key={task.id}>
                    {/*
                      SELURUH baris satu tautan, bukan hanya judulnya. Sebelumnya hanya teks judul
                      yang dapat diketuk — sasaran selebar beberapa kata, pada layar yang dipakai
                      satu tangan sambil berjalan.
                    */}
                    <Link
                      to={`/tasks/${task.id}`}
                      className="task-row"
                      state={{ asal: ASAL_TUGAS_SAYA }}
                    >
                      <span
                        className={`task-mark mark-${taskMark(task.status)}`}
                        aria-hidden="true"
                      />
                      <span className="task-body">
                        <span className="row space-between" style={{ gap: '0.6rem' }}>
                          <strong
                            className={taskStruck(task.status) ? 'task-title struck' : 'task-title'}
                          >
                            {task.title}
                          </strong>
                          <span className={`badge status-${task.status}`}>
                            {formatTaskStatus(task.status)}
                          </span>
                        </span>
                        <span className="muted small task-meta">
                          {task.taskNumber} · {formatPriority(task.priority)}
                        </span>
                        {task.destinationName || task.destinationAddress ? (
                          <span className="small task-meta">
                            {task.destinationName ?? task.destinationAddress}
                          </span>
                        ) : null}
                        <span className="muted small task-meta">
                          {task.scheduledWindowStart
                            ? `Jadwal ${formatDateInZone(task.scheduledWindowStart, timezone)} ${formatTimeInZone(task.scheduledWindowStart, timezone)}${
                                task.scheduledWindowEnd
                                  ? `\u2013${formatTimeInZone(task.scheduledWindowEnd, timezone)}`
                                  : ''
                              }`
                            : 'Tanpa jadwal tetap'}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            );
          })()
        : null}
    </>
  );
}
